import React, { useMemo } from "react";
import { useLeads } from "@/context/LeadContext";
import { usePartners } from "@/context/PartnerContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format, subDays, differenceInDays, isAfter } from "date-fns";
import { el } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from "@/components/ui/chart";
import {
  Users,
  Briefcase,
  UserCheck,
  Clock,
  MapPin,
  TrendingUp,
  BarChart2,
  Filter,
  Timer,
  AlertTriangle,
  CheckCircle,
} from "lucide-react";
import StatisticsCard from "@/components/dashboard/StatisticsCard";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Toggle } from "@/components/ui/toggle";
import { PageHeader } from "@/components/layouts/PageHeader";

interface ChartDataPoint {
  date: string;
  leads: number;
  assigned: number;
  completed: number;
}

const LeadStatistics = () => {
  const { leads } = useLeads();
  const { partners } = usePartners();
  const [timeRange, setTimeRange] = React.useState<number>(30);
  const [showAccumulated, setShowAccumulated] = React.useState<boolean>(false);
  const [selectedServices, setSelectedServices] = React.useState<Record<string, boolean>>({
    εξοικονομώ: true,
    αντλίεςΘερμότητας: true,
    φωτοβολταϊκά: true,
    κλιματιστικά: true,
    φυσικόΑέριο: true,
    ηλιακά: true,
    άλλο: true
  });

  const serviceLabels: Record<string, string> = {
    εξοικονομώ: "Εξοικονομώ",
    αντλίεςΘερμότητας: "Αντλίες Θερμότητας",
    φωτοβολταϊκά: "Φωτοβολταϊκά",
    κλιματιστικά: "Κλιματιστικά",
    φυσικόΑέριο: "Φυσικό Αέριο",
    ηλιακά: "Ηλιακά",
    άλλο: "Άλλο"
  };

  const serviceColors: Record<string, string> = {
    εξοικονομώ: "#3b82f6",
    αντλίεςΘερμότητας: "#10b981",
    φωτοβολταϊκά: "#f59e0b",
    κλιματιστικά: "#6366f1",
    φυσικόΑέριο: "#ef4444",
    ηλιακά: "#14b8a6",
    άλλο: "#9333ea"
  };

  const filteredLeads = useMemo(() => {
    const startDate = subDays(new Date(), timeRange);

    return leads.filter(lead => {
      const leadDate = new Date(lead.createdAt);
      const isInDateRange = isAfter(leadDate, startDate);
      const isSelectedService = selectedServices[lead.service];

      return isInDateRange && isSelectedService;
    });
  }, [leads, timeRange, selectedServices]);

  const dailyLeadsData = useMemo(() => {
    if (filteredLeads.length === 0) return [];

    const dateMap: Record<string, ChartDataPoint> = {};

    for (let i = 0; i <= timeRange; i++) {
      const date = subDays(new Date(), timeRange - i);
      const dateStr = format(date, "yyyy-MM-dd");
      const displayDate = format(date, "d MMM", { locale: el });

      dateMap[dateStr] = {
        date: displayDate,
        leads: 0,
        assigned: 0,
        completed: 0
      };
    }

    filteredLeads.forEach(lead => {
      const leadDate = format(new Date(lead.createdAt), "yyyy-MM-dd");
      if (dateMap[leadDate]) {
        dateMap[leadDate].leads += 1;
        if (lead.partnerId) dateMap[leadDate].assigned += 1;
        if (lead.stage === "έκλεισεΗΔουλειά") dateMap[leadDate].completed += 1;
      }
    });

    const dateArray = Object.values(dateMap);

    if (showAccumulated) {
      let totalLeads = 0;
      let totalAssigned = 0;
      let totalCompleted = 0;

      return dateArray.map(item => {
        totalLeads += item.leads;
        totalAssigned += item.assigned;
        totalCompleted += item.completed;

        return {
          date: item.date,
          leads: totalLeads,
          assigned: totalAssigned,
          completed: totalCompleted
        };
      });
    }

    return dateArray;
  }, [filteredLeads, timeRange, showAccumulated]);

  const serviceDistribution = useMemo(() => {
    const distribution: Record<string, number> = {};

    filteredLeads.forEach(lead => {
      distribution[lead.service] = (distribution[lead.service] || 0) + 1;
    });

    return Object.entries(distribution).map(([service, count]) => ({
      name: serviceLabels[service] || service,
      value: count,
      color: serviceColors[service] || "#777"
    }));
  }, [filteredLeads]);

  const timeframeDistribution = useMemo(() => {
    const distribution: Record<string, number> = {
      'άμεσα': 0,
      'εντός3μηνών': 0,
      'κάνωΈρευνα': 0
    };

    filteredLeads.forEach(lead => {
      distribution[lead.timeframe] = (distribution[lead.timeframe] || 0) + 1;
    });

    const timeframeLabels: Record<string, string> = {
      'άμεσα': 'Άμεσα',
      'εντός3μηνών': 'Εντός 3 μηνών',
      'κάνωΈρευνα': 'Κάνω έρευνα'
    };

    const timeframeColors: Record<string, string> = {
      'άμεσα': '#ef4444',
      'εντός3μηνών': '#f59e0b',
      'κάνωΈρευνα': '#3b82f6'
    };

    return Object.entries(distribution).map(([timeframe, count]) => ({
      name: timeframeLabels[timeframe] || timeframe,
      value: count,
      color: timeframeColors[timeframe] || "#777"
    }));
  }, [filteredLeads]);

  const geographicalDistribution = useMemo(() => {
    const postalCodes: Record<string, { count: number, areas: Set<string> }> = {};

    filteredLeads.forEach(lead => {
      const postalCode = lead.postalCode;
      if (!postalCode) return;

      if (!postalCodes[postalCode]) {
        postalCodes[postalCode] = { count: 0, areas: new Set() };
      }

      postalCodes[postalCode].count += 1;
      postalCodes[postalCode].areas.add(lead.address.split(',')[0]);
    });

    return Object.entries(postalCodes)
      .map(([postalCode, data]) => ({
        name: `${postalCode}`,
        value: data.count
      }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [filteredLeads]);

  const partnerAssignmentStats = useMemo(() => {
    const partnerStats: Record<string, { assigned: number, completed: number, name: string }> = {};

    filteredLeads.forEach(lead => {
      if (!lead.partnerId) return;

      if (!partnerStats[lead.partnerId]) {
        const partner = partners.find(p => p.id === lead.partnerId);
        partnerStats[lead.partnerId] = {
          assigned: 0,
          completed: 0,
          name: partner ? partner.name : `Partner ID: ${lead.partnerId}`
        };
      }

      partnerStats[lead.partnerId].assigned += 1;
      if (lead.stage === "έκλεισεΗΔουλειά") {
        partnerStats[lead.partnerId].completed += 1;
      }
    });

    return Object.values(partnerStats)
      .sort((a, b) => b.assigned - a.assigned)
      .map(stat => ({
        name: stat.name.length > 15 ? stat.name.substring(0, 15) + '...' : stat.name,
        assigned: stat.assigned,
        completed: stat.completed,
        ratio: stat.assigned > 0 ? Math.round((stat.completed / stat.assigned) * 100) : 0
      }));
  }, [filteredLeads, partners]);

  const stats = useMemo(() => {
    const totalLeads = filteredLeads.length;
    const assignedLeads = filteredLeads.filter(lead => lead.partnerId).length;
    const assignmentRate = totalLeads > 0 ? Math.round((assignedLeads / totalLeads) * 100) : 0;
    const completedLeads = filteredLeads.filter(lead => lead.stage === "έκλεισεΗΔουλειά").length;
    const completionRate = assignedLeads > 0 ? Math.round((completedLeads / assignedLeads) * 100) : 0;

    let totalAssignmentTime = 0;
    let assignedLeadsCount = 0;

    filteredLeads.forEach(lead => {
      if (lead.partnerId && lead.updatedAt && lead.createdAt) {
        const creationDate = new Date(lead.createdAt);
        const updateDate = new Date(lead.updatedAt);
        const daysDiff = differenceInDays(updateDate, creationDate);
        if (daysDiff >= 0) {
          totalAssignmentTime += daysDiff;
          assignedLeadsCount++;
        }
      }
    });

    const avgAssignmentTime = assignedLeadsCount > 0 ?
      Math.round((totalAssignmentTime / assignedLeadsCount) * 10) / 10 : 0;

    const prevPeriodStart = subDays(new Date(), timeRange * 2);
    const currentPeriodStart = subDays(new Date(), timeRange);

    const currentPeriodLeads = leads.filter(lead =>
      isAfter(new Date(lead.createdAt), currentPeriodStart)
    ).length;

    const prevPeriodLeads = leads.filter(lead =>
      isAfter(new Date(lead.createdAt), prevPeriodStart) &&
      !isAfter(new Date(lead.createdAt), currentPeriodStart)
    ).length;

    let trend = 0;
    if (prevPeriodLeads > 0) {
      trend = Math.round(((currentPeriodLeads - prevPeriodLeads) / prevPeriodLeads) * 100);
    }

    return {
      totalLeads,
      assignedLeads,
      assignmentRate,
      completedLeads,
      completionRate,
      avgAssignmentTime,
      trend
    };
  }, [filteredLeads, leads, timeRange]);

  const operationalStats = useMemo(() => {
    const newLeads = filteredLeads.filter(lead => lead.stage === "Νέο").length;
    const pendingLeads = filteredLeads.filter(lead => lead.stage === "σεΕκκρεμότητα").length;
    const infoLeads = filteredLeads.filter(lead => lead.stage === "ήθελεΠληροφορίες").length;
    const unassignedLeads = filteredLeads.filter(lead => !lead.partnerId).length;
    const stalePendingLeads = filteredLeads.filter(lead => {
      if (lead.stage !== "σεΕκκρεμότητα") return false;
      const ageHours = (Date.now() - new Date(lead.createdAt).getTime()) / (1000 * 60 * 60);
      return ageHours >= 48;
    }).length;

    return {
      newLeads,
      pendingLeads,
      infoLeads,
      unassignedLeads,
      stalePendingLeads,
    };
  }, [filteredLeads]);

  const chartConfig = {
    leads: { label: "Επαφές", theme: { light: "#3b82f6", dark: "#60a5fa" } },
    assigned: { label: "Ανατεθείσες", theme: { light: "#10b981", dark: "#34d399" } },
    completed: { label: "Ολοκληρωμένες", theme: { light: "#f59e0b", dark: "#fbbf24" } }
  };

  const handleServiceToggle = (service: string) => {
    setSelectedServices(prev => ({ ...prev, [service]: !prev[service] }));
  };

  return (
    <div className="space-y-8 animate-fade-in">
      <PageHeader
        title="Στατιστικά Επαφών"
        description="Αναλυτικά στοιχεία, τάσεις και σημεία προτεραιότητας"
        icon={<BarChart2 />}
        iconGradient="purple"
      />

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-slate-100">
              <Filter className="h-4 w-4 text-slate-600" />
            </div>
            <div>
              <CardTitle className="text-lg">Φίλτρα</CardTitle>
              <CardDescription>Επιλέξτε χρονικό διάστημα και υπηρεσίες</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-slate-700 mb-2">Χρονικό διάστημα</h3>
                <div className="flex flex-wrap gap-2">
                  {[7, 30, 90, 365].map(days => (
                    <Toggle
                      key={days}
                      variant="outline"
                      size="sm"
                      pressed={timeRange === days}
                      onPressedChange={() => setTimeRange(days)}
                      className="data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
                    >
                      {days === 365 ? '1 έτος' : `${days} ημέρες`}
                    </Toggle>
                  ))}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="accumulated" checked={showAccumulated} onCheckedChange={setShowAccumulated} />
                <label htmlFor="accumulated" className="text-sm text-slate-600">Συσσωρευτικές τιμές</label>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">Υπηρεσίες</h3>
              <div className="flex flex-wrap gap-2">
                {Object.keys(serviceLabels).map(service => (
                  <Toggle
                    key={service}
                    variant="outline"
                    size="sm"
                    pressed={selectedServices[service]}
                    onPressedChange={() => handleServiceToggle(service)}
                    className="data-[state=on]:bg-blue-100 data-[state=on]:text-blue-700"
                  >
                    <div className="w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: serviceColors[service] }} />
                    {serviceLabels[service]}
                  </Toggle>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatisticsCard
          title="Συνολικές Επαφές"
          value={stats.totalLeads}
          icon={<Users />}
          variant="info"
          trend={stats.trend !== 0 ? { value: stats.trend, isPositive: stats.trend > 0 } : undefined}
          description={`Τελευταίες ${timeRange} ημέρες`}
        />
        <StatisticsCard
          title="Ανατεθειμένες"
          value={stats.assignedLeads}
          icon={<UserCheck />}
          variant="success"
          description={`${stats.assignmentRate}% ανάθεσης`}
        />
        <StatisticsCard
          title="Ολοκληρωμένες"
          value={stats.completedLeads}
          icon={<Briefcase />}
          variant={stats.completionRate > 50 ? "success" : "warning"}
          description={`${stats.completionRate}% ολοκλήρωση`}
        />
        <StatisticsCard
          title="Μέσος Χρόνος"
          value={`${stats.avgAssignmentTime} ημ.`}
          icon={<Clock />}
          variant={stats.avgAssignmentTime < 1 ? "success" : stats.avgAssignmentTime < 3 ? "warning" : "danger"}
          description="Ανάθεσης"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="border-slate-200 lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pipeline Snapshot</CardTitle>
            <CardDescription>Τρέχουσα κατανομή ροής lead</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            <Badge variant="outline" className="text-blue-700 border-blue-200 bg-blue-50">
              Νέα: {operationalStats.newLeads}
            </Badge>
            <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50">
              Εκκρεμότητα: {operationalStats.pendingLeads}
            </Badge>
            <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50">
              Πληροφορίες: {operationalStats.infoLeads}
            </Badge>
            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">
              Κλεισμένα: {stats.completedLeads}
            </Badge>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Alerts</CardTitle>
            <CardDescription>Λειτουργικά σημεία προσοχής</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex items-center justify-between border-l-4 border-amber-400 bg-amber-50 px-3 py-2.5 rounded-r-md">
              <span className="text-sm text-amber-800">Χωρίς ανάθεση</span>
              <span className="font-semibold text-amber-900">{operationalStats.unassignedLeads}</span>
            </div>
            <div className="flex items-center justify-between border-l-4 border-orange-400 bg-orange-50 px-3 py-2.5 rounded-r-md">
              <span className="text-sm text-orange-800">Εκκρεμότητα 48h+</span>
              <span className="font-semibold text-orange-900">{operationalStats.stalePendingLeads}</span>
            </div>
            {operationalStats.stalePendingLeads > 0 ? (
              <Badge variant="outline" className="w-full justify-center text-orange-700 border-orange-200 bg-orange-50">
                <AlertTriangle className="h-3.5 w-3.5 mr-1" /> Απαιτείται follow-up
              </Badge>
            ) : (
              <Badge variant="outline" className="w-full justify-center text-emerald-700 border-emerald-200 bg-emerald-50">
                <CheckCircle className="h-3.5 w-3.5 mr-1" /> Καμία κρίσιμη εκκρεμότητα
              </Badge>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="timeline">
        <TabsList className="bg-slate-100 p-1 rounded-lg">
          <TabsTrigger value="timeline" className="rounded-md data-[state=active]:bg-white">Χρονική Εξέλιξη</TabsTrigger>
          <TabsTrigger value="services" className="rounded-md data-[state=active]:bg-white">Υπηρεσίες</TabsTrigger>
          <TabsTrigger value="geographical" className="rounded-md data-[state=active]:bg-white">Γεωγραφική</TabsTrigger>
          <TabsTrigger value="partners" className="rounded-md data-[state=active]:bg-white">Συνεργάτες</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-50">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Εξέλιξη Επαφών</CardTitle>
                  <CardDescription>{showAccumulated ? "Συσωρευτική" : "Ημερήσια"} καταγραφή</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-96">
              <ChartContainer config={chartConfig}>
                <ResponsiveContainer width="100%" height="100%">
                  {showAccumulated ? (
                    <AreaChart data={dailyLeadsData}>
                      <defs>
                        <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorAssigned" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Legend />
                      <Area type="monotone" dataKey="leads" name="Επαφές" stroke="#3b82f6" fillOpacity={1} fill="url(#colorLeads)" />
                      <Area type="monotone" dataKey="assigned" name="Ανατεθείσες" stroke="#10b981" fillOpacity={1} fill="url(#colorAssigned)" />
                    </AreaChart>
                  ) : (
                    <BarChart data={dailyLeadsData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                      <ChartTooltip content={<ChartTooltipContent />} />
                      <Legend />
                      <Bar dataKey="leads" name="Επαφές" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="assigned" name="Ανατεθείσες" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  )}
                </ResponsiveContainer>
              </ChartContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="services" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-violet-50">
                    <Briefcase className="h-4 w-4 text-violet-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Κατανομή Υπηρεσιών</CardTitle>
                    <CardDescription>Ανά τύπο υπηρεσίας</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={serviceDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                      {serviceDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg bg-amber-50">
                    <Timer className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Χρονικό Πλαίσιο</CardTitle>
                    <CardDescription>Επείγουσα υλοποίησης</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={timeframeDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={85} paddingAngle={3}>
                      {timeframeDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="geographical" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-purple-50">
                  <MapPin className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Γεωγραφική Κατανομή</CardTitle>
                  <CardDescription>Top 10 ταχυδρομικοί κωδικοί</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={geographicalDistribution} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <YAxis type="category" dataKey="name" width={60} tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="value" name="Επαφές" fill="#9333ea" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="partners" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-emerald-50">
                  <Users className="h-4 w-4 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Επιδόσεις Συνεργατών</CardTitle>
                  <CardDescription>Αναθέσεις και ολοκληρώσεις</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={partnerAssignmentStats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} stroke="#94a3b8" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#94a3b8" />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Legend />
                  <Bar dataKey="assigned" name="Ανατεθείσες" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="completed" name="Ολοκληρωμένες" fill="#10b981" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default LeadStatistics;
